<template>
  <div class="wizard-overlay">
    <!-- Backdrop -->
    <div
      class="wizard-backdrop"
      @click="handleClose" />

    <!-- Modal Panel -->
    <div class="absolute inset-0 overflow-hidden">
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="wizard-modal" :class="{ 'max-w-3xl': isImportMode }">
          <!-- Loading State -->
          <div v-if="loading" class="flex h-96 items-center justify-center">
            <div class="text-center">
              <svg
                class="wizard-spinner"
                fill="none"
                viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4" />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p class="mt-4 text-body" style="color: var(--text-secondary);">
                Loading...
              </p>
            </div>
          </div>

          <!-- Import Mode: URL Input -->
          <div v-else-if="importMode === 'input'" class="p-6">
            <div class="flex justify-end mb-4">
              <button
                type="button"
                class="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                @click="handleClose">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <UrlInput
              :disabled="importState.isImporting.value"
              :error="importState.error.value"
              @import="handleStartImport"
              @skip="handleSkipImport"
              @choose-template="handleChooseTemplate" />
          </div>

          <!-- Import Mode: Progress -->
          <div v-else-if="importMode === 'progress'" class="p-6">
            <div class="flex justify-end mb-4">
              <button
                type="button"
                class="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                @click="handleClose">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ImportProgress
              :current-step="importState.currentStep.value"
              :progress="importState.progress.value" />
          </div>

          <!-- Import Mode: Review -->
          <div v-else-if="importMode === 'review'" class="p-6">
            <div class="flex justify-end mb-4">
              <button
                type="button"
                class="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                @click="handleClose">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ReviewModal
              :data="importState.normalizedData.value"
              :screenshots="importState.screenshots.value"
              :applying="applyingImport"
              @apply="handleApplyImport"
              @cancel="handleCancelImport" />
          </div>

          <!-- Wizard Content -->
          <div v-else>
            <!-- Header -->
            <WizardHeader
              :current-step="currentStep"
              :total-steps="totalSteps"
              :step-title="currentStepTitle"
              :progress="progressPercentage"
              @close="handleClose" />

            <!-- Step Content -->
            <div class="wizard-content">
              <KeepAlive>
                <component
                  :is="currentStepComponent"
                  :wizard="wizard"
                  :map-key="mapKey" />
              </KeepAlive>
            </div>

            <!-- Footer Navigation -->
            <WizardNavigation
              :is-first-step="isFirstStep"
              :is-last-step="isLastStep"
              :can-proceed="canProceed"
              :saving="saving"
              @prev="handlePrev"
              @next="handleNext"
              @complete="handleComplete" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, markRaw, watch } from 'vue'
import useWizard from '~/javascript/composables/use_wizard'
import useImport from '~/javascript/composables/use_import'
import WizardHeader from './wizard_header.vue'
import WizardNavigation from './wizard_navigation.vue'
import BusinessBasicsStep from './steps/business_basics.vue'
import ServicesStep from './steps/services.vue'
import ServiceAreasStep from './steps/service_areas.vue'
import GalleryStep from './steps/gallery.vue'
import UrlInput from './url_import/url_input.vue'
import ImportProgress from './url_import/import_progress.vue'
import ReviewModal from './url_import/review_modal.vue'

const props = defineProps({
  mapKey: { type: String, required: true },
  websiteId: { type: String, required: true },
  initialStep: { type: Number, default: 1 },
  onClose: { type: Function, required: true },
  onComplete: { type: Function, required: true }
})

// Import mode state: 'input' | 'progress' | 'review' | null (wizard mode)
// Pre-set to 'input' if starting at step 1 (new website) to prevent modal width jolt
// Will be corrected after loading if website already has completed steps
const importMode = ref(props.initialStep === 1 ? 'input' : null)
const applyingImport = ref(false)

// Initialize wizard composable with websiteId
const wizard = useWizard(props.websiteId, props.initialStep)

const {
  currentStep,
  loading,
  saving,
  totalSteps,
  currentStepTitle,
  isFirstStep,
  isLastStep,
  progressPercentage,
  canProceed,
  initialize,
  nextStep,
  prevStep,
  completeCurrentStep,
  website,
  loadWebsite,
  saveWebsite: _saveWebsite,
  services: _services,
  loadServices,
  loadServiceAreas,
  loadGalleryImages,
  setLoading
} = wizard

// Initialize import composable
const importState = useImport(props.websiteId)

// Computed
const isImportMode = computed(() => importMode.value !== null)

// Map steps to components
const stepComponents = {
  1: markRaw(BusinessBasicsStep),
  2: markRaw(ServicesStep),
  3: markRaw(ServiceAreasStep),
  4: markRaw(GalleryStep)
}

const currentStepComponent = computed(() => stepComponents[currentStep.value])

// Watch import status for transitions
watch(() => importState.status.value, (status) => {
  if (importMode.value === 'progress') {
    if (status === 'completed') {
      importMode.value = 'review'
    } else if (status === 'failed') {
      // Go back to input mode on failure
      importMode.value = 'input'
    }
  }
})

// Event handlers
function handleClose() {
  importState.cleanup()
  props.onClose()
}

async function handlePrev() {
  prevStep()
}

async function handleNext() {
  try {
    await nextStep()
  } catch (err) {
    console.error('Failed to proceed to next step:', err)
  }
}

async function handleComplete() {
  try {
    await completeCurrentStep()
    props.onComplete()
  } catch (err) {
    console.error('Failed to complete wizard:', err)
  }
}

// Import handlers
async function handleStartImport(url) {
  try {
    await importState.startImport(url)
    importMode.value = 'progress'
  } catch (err) {
    console.error('Failed to start import:', err)
  }
}

function handleSkipImport() {
  importMode.value = null
}

function handleChooseTemplate() {
  // Templates gallery lives outside the modal wizard at a dashboard route.
  // The page renders inline-disclosure cards with apply forms per template.
  window.location.href = `/dashboard/website/${props.websiteId}/templates`
}

async function handleApplyImport(editedData) {
  applyingImport.value = true

  try {
    // Apply the import via API with user's edited data
    await importState.applyImport(editedData)

    // Reload all wizard data after import
    await loadWebsite()
    await loadServices()
    await loadServiceAreas()
    await loadGalleryImages()

    // Close import mode and show step 1
    importMode.value = null
    importState.reset()
  } catch (err) {
    console.error('Failed to apply import:', err)
    // Check if the import no longer exists (404)
    if (err.response?.status === 404) {
      alert('This import session has expired. Please start a new import.')
      importState.reset()
      importMode.value = 'input'
    } else {
      alert(`Failed to apply import: ${err.response?.data?.error || err.message}`)
    }
  } finally {
    applyingImport.value = false
  }
}

function handleCancelImport() {
  importState.reset()
  importMode.value = 'input'
}

// Initialize on mount
onMounted(async() => {
  // Use manageLoading: false so we control when loading ends
  // This prevents flash of wizard content before importMode is determined
  await initialize({ manageLoading: false })

  // Verify import mode after loading - correct the pre-set if needed
  // Show import mode only for new websites (no completed steps yet)
  const completedSteps = website.wizard_completed_steps || []
  const shouldShowImport = completedSteps.length === 0 && !website.imported_from_url

  if (shouldShowImport) {
    importMode.value = 'input'
  } else {
    // Website has progress or was imported - switch to wizard mode
    importMode.value = null
  }

  // Now that importMode is finalized, hide the loading spinner
  // Vue will render the correct content (import or wizard) in a single update
  setLoading(false)
})

// Cleanup on unmount
onUnmounted(() => {
  importState.cleanup()
})
</script>

<style scoped>
.wizard-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  overflow: hidden;
}

.wizard-backdrop {
  position: absolute;
  inset: 0;
  background-color: rgb(var(--color-gray-900-rgb) / 0.5);
  transition: opacity var(--duration-200) var(--ease-in-out);
}

.wizard-modal {
  position: relative;
  width: 100%;
  max-width: var(--container-md);
  transform: scale(1);
  overflow: hidden;
  border-radius: var(--radius-2xl);
  background-color: var(--bg-surface);
  box-shadow: var(--shadow-xl);
  transition: all var(--duration-200) var(--ease-in-out);
}

.wizard-spinner {
  margin: 0 auto;
  width: 3rem;
  height: 3rem;
  animation: spin 1s linear infinite;
  color: var(--text-primary);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.wizard-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: var(--space-6);
  background-color: var(--bg-surface);
}
</style>
